import React, { useMemo } from 'react';
import { Box, Divider, Paper, Typography } from '@mui/material';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import Loading from '../../../components/loading/Loading';
import { IndicadorStatusPedidosChart } from '../IndicadoresGraficos.types';
import './StatusPedidosBarChartCard.css';

type StatusPedidosBarChartCardProps = {
    isLoading: boolean;
    dados: IndicadorStatusPedidosChart[];
};

type DadoStatusBarra = {
    statusPedido: string;
    quantidadePedidos: number;
    percentualFinal: number;
};

const ORDEM_STATUS = [
    'faturado',
    'sincronizado',
    'pedido',
    'internalizado',
    'revisar',
    'sincronizar',
    'cancelado',
    'orcamento',
    'rascunho',
];

const CORES_POR_STATUS: Record<string, string> = {
    faturado: '#27ae60',
    sincronizado: '#2c3e50',
    pedido: '#3498db',
    internalizado: '#2980b9',
    revisar: '#f1c40f',
    sincronizar: '#e67e22',
    cancelado: '#7f8c8d',
    orcamento: '#95a5a6',
    rascunho: '#bdc3c7',
};

const COR_PADRAO_STATUS = '#6b7280';

function normalizarStatus(status: string): string {
    return status
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
}

function resolverChaveStatus(status: string): string {
    const statusNormalizado = normalizarStatus(status);

    if (CORES_POR_STATUS[statusNormalizado]) {
        return statusNormalizado;
    }

    const prefixoAntesHifen = statusNormalizado.split('-')[0].trim();
    if (CORES_POR_STATUS[prefixoAntesHifen]) {
        return prefixoAntesHifen;
    }

    const statusConhecido = ORDEM_STATUS.find((statusBase) => statusNormalizado.includes(statusBase));
    return statusConhecido || statusNormalizado;
}

function obterCorStatus(status: string): string {
    return CORES_POR_STATUS[resolverChaveStatus(status)] || COR_PADRAO_STATUS;
}

function formatarPercentual(valor: number): string {
    if (!Number.isFinite(valor)) {
        return '0%';
    }

    return `${valor.toFixed(valor % 1 === 0 ? 0 : 1)}%`;
}

function StatusPedidosBarChartCard({ isLoading, dados }: StatusPedidosBarChartCardProps) {
    const dadosOrdenados = useMemo<DadoStatusBarra[]>(() => {
        const dadosValidos = dados.filter((item) => Number(item.quantidadePedidos || 0) > 0);
        const totalPedidos = dadosValidos.reduce((acc, item) => acc + Number(item.quantidadePedidos || 0), 0);

        return [...dadosValidos]
            .map((item) => {
                const percentualCalculado = totalPedidos > 0
                    ? (Number(item.quantidadePedidos || 0) / totalPedidos) * 100
                    : 0;

                return {
                    statusPedido: item.statusPedido,
                    quantidadePedidos: Number(item.quantidadePedidos || 0),
                    percentualFinal: Number(item.percentual || 0) > 0 ? Number(item.percentual || 0) : percentualCalculado,
                };
            })
            .sort((a, b) => {
                const diferencaQuantidade = b.quantidadePedidos - a.quantidadePedidos;

                if (diferencaQuantidade !== 0) {
                    return diferencaQuantidade;
                }

                return a.statusPedido.localeCompare(b.statusPedido, 'pt-BR');
            });
    }, [dados]);

    const quantidadeCategorias = dadosOrdenados.length;
    const maxBarSize = quantidadeCategorias <= 3 ? 12 : quantidadeCategorias <= 6 ? 16 : 20;
    const barCategoryGap = quantidadeCategorias <= 3 ? '42%' : quantidadeCategorias <= 6 ? '26%' : '16%';

    return (
        <Paper elevation={0} className='indicadores-paper indicadores-chart-paper'>
            <Box className='indicadores-chart-header'>
                <Box>
                    <Typography className='indicadores-chart-title'>Pedidos por Status</Typography>
                    <Typography className='indicadores-chart-subtitle'>
                        Quantidade de pedidos por status no período selecionado.
                    </Typography>
                </Box>
            </Box>
            <Box className='indicadores-status-bar-spacer' />
            <Divider className='indicadores-divider' />

            {isLoading ? (
                <Loading />
            ) : dadosOrdenados.length === 0 ? (
                <Box className='indicadores-empty-state'>
                    <Box>
                        <QueryStatsRoundedIcon className='indicadores-empty-icon' />
                        <Typography className='indicadores-empty-title'>
                            Sem dados de status para o período
                        </Typography>
                    </Box>
                </Box>
            ) : (
                <Box className='indicadores-chart-wrap indicadores-chart-wrap-status-bar'>
                    <Box className='indicadores-chart-content'>
                        <ResponsiveContainer width='100%' height={200}>
                            <BarChart
                                data={dadosOrdenados}
                                layout='vertical'
                                margin={{ top: 0, right: 32, left: 8, bottom: 0 }}
                                barCategoryGap={barCategoryGap}
                            >
                                <CartesianGrid strokeDasharray='3 3' stroke='#e8eef3' />
                                <XAxis type='number' allowDecimals={false} tick={{ fontSize: 11 }} />
                                <YAxis type='category' dataKey='statusPedido' width={0} tick={false} />
                                <Tooltip
                                    formatter={(valor, _nome, entry) => {
                                        const dado = (entry as { payload?: DadoStatusBarra })?.payload;
                                        return [
                                            `${Number(valor || 0)} pedidos (${formatarPercentual(dado?.percentualFinal || 0)})`,
                                            dado?.statusPedido || 'Status',
                                        ];
                                    }}
                                    contentStyle={{
                                        borderRadius: 10,
                                        border: '1px solid #d5e2eb',
                                        backgroundColor: '#f8fbfe',
                                    }}
                                />
                                <Bar dataKey='quantidadePedidos' radius={[0, 3, 3, 0]} maxBarSize={maxBarSize}>
                                    <LabelList
                                        dataKey='quantidadePedidos'
                                        position='right'
                                        fill='#4b6577'
                                        fontSize={11}
                                    />
                                    {dadosOrdenados.map((item) => (
                                        <Cell key={item.statusPedido} fill={obterCorStatus(item.statusPedido)} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>

                    <Box className='indicadores-status-bar-legenda'>
                        <Box className='indicadores-status-bar-legenda-inner'>
                            {dadosOrdenados.map((item) => (
                                <Box key={`legenda-${item.statusPedido}`} className='indicadores-status-bar-legenda-item'>
                                    <Box
                                        className='indicadores-status-bar-legenda-dot'
                                        sx={{ backgroundColor: obterCorStatus(item.statusPedido) }}
                                    />
                                    <Typography component='span' sx={{ fontSize: 'inherit', color: 'inherit' }}>
                                        {item.statusPedido}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            )}
        </Paper>
    );
}

export default React.memo(StatusPedidosBarChartCard);
